import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useGolfLeagueContext } from '../context/GolfLeagueContext';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useNavigateToTop from '../../../hooks/general/useNavigateToTop';
import LoadingScreen from '../../LoadingScreen/LoadingScreen';
import { useSearchParams } from 'react-router-dom';
import TeamScoreEntry from './TeamScoreEntry';
import { isEqual } from 'lodash';
import ScoreSubmitBar from './ScoreSubmitBar';


const ScoreEntryPage = () => {
    const { 
        gameData, 
        myUserId,
        gameAddress,
        gameId,
        masterPoolId, 
        masterPoolData,
    } = useUserAndGameContext();

    const {
        teams,
        playerState,
        players,
        setPlayerState,
        events,
        currentInterval,
        intervalsToShow,
    } = useGolfLeagueContext();
    //console.log("Teams", teams);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [invalidEntries, setInvalidEntries] = useState([]);
    const [completedTeams, setCompletedTeams] = useState([]);
    const [intitialCheck, setIntitialCheck] = useState(false);
    //console.log("Completed Teams", completedTeams);
    //console.log("Invalid Entries", invalidEntries);

    // Check to see if the playerState is the same as the players using lodash
    const isPlayerStateEqual = useMemo(() => {
        return isEqual(playerState, players);
    }, [playerState, players]);
    //console.log("Is Player State Equal", isPlayerStateEqual);

    const [searchParams, setSearchParams] = useSearchParams();
    const paramEventId = searchParams.get('eventId');
    const paramRound = searchParams.get('round') || '1';

    const navigateToTop = useNavigateToTop();

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    const admins = gameData?.gameAdministration?.administrators;
    const isAdmin = admins?.includes(myUserId);

    //console.log("Is Admin", isAdmin, "Admins", admins, "My User ID", myUserId);

    const scoreEntryPageInfo = masterPoolData?.pageInfo?.scoreEntry;
    const pageTitle = scoreEntryPageInfo?.pageTitle || "Score Entry";
    const textColor = scoreEntryPageInfo?.textColor || "#002129";

    // Create dropdown options for tournaments (without Overall for individual view)
    const eventOptions = useMemo(() => {
        // Create options from intervalsToShow
        return intervalsToShow?.map((interval) => {
        const event = events?.find((event) => event.interval === interval);
        return {
            value: interval,
            label: event ? event.name : `Interval ${interval}`,
            tournamentId: event?.tournamentId || String(interval),
        };
        }) || [];
    }, [intervalsToShow, events]);
    //console.log("Event Options", eventOptions);

    // Use Effect to ensure URL has valid parameters when component mounts
    useEffect(() => {
        if (eventOptions.length === 0) return;
      
        const newSearchParams = new URLSearchParams(searchParams);
        let needsUpdate = false;
      
        const roundParam = searchParams.get('round');
        const roundNum = Number(roundParam);
      
        // Use Number conversion to ensure consistent type comparison
        const validEventId = paramEventId && eventOptions.some(option => 
            Number(option.tournamentId) === Number(paramEventId)
        );
        
        if (!validEventId) {
          newSearchParams.set('eventId', String(eventOptions[0]?.tournamentId));
          needsUpdate = true;
        }
      
        // Also use Number conversion here to ensure proper event finding
        const selectedEvent = events?.find(event =>
          Number(event.tournamentId) === Number(validEventId ? paramEventId : eventOptions[0]?.tournamentId)
        );
        
        const maxRounds = selectedEvent?.rounds || 1;
        
        if (!roundParam || isNaN(roundNum) || roundNum < 1 || roundNum > maxRounds) {
          newSearchParams.set('round', '1');
          needsUpdate = true;
        }
      
        if (needsUpdate) {
          setSearchParams(newSearchParams);
        }
    }, [paramEventId, paramRound, eventOptions, events, setSearchParams]);
      

    const selectedEvent = useMemo(() => {
        return events?.find(event => Number(event.tournamentId) === Number(paramEventId));
    }, [events, paramEventId]);
    //console.log("Selected Event", selectedEvent);

    // Handle tournament dropdown change
    const handleTournamentChange = (event) => {
        const newEventId = event.target.value;
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('eventId', String(newEventId));
        setSearchParams(newSearchParams);
    };

    useEffect(() => {
        if (!isAdmin) {
            const url = `/${uriEncoded(gameAddress)}?mId=${uriEncoded(masterPoolId)}&gId=${uriEncoded(gameId)}&page=home`;
            //console.log("Not an admin, navigating to:", url);
            navigateToTop(url);
        }
    }, [isAdmin, gameAddress, masterPoolId, gameId, navigateToTop]);
    
    // If not admin, return null or a loading indicator while redirect happens
    if (!isAdmin) {
        return <LoadingScreen />;
    }

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '40px',
                    fontWeight: '800',
                    margin: '24px 0',
                    textAlign: 'center',
                    color: textColor,
                }}
            >
                {pageTitle}
            </Typography>

            {/* Dropdown of entryOptions (Tournament ONLY) */}
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    gap: '16px',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: textColor,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Tournament:
                </Typography>
                
                {/* Tournament Dropdown */}
                <Box sx={{ width: isMobile ? '60%' : '300px' }}>
                    <FormControl fullWidth variant="outlined">
                        <Select
                            id="tournament-select"
                            value={paramEventId || ''}
                            onChange={handleTournamentChange}
                            displayEmpty
                            sx={{
                                fontSize: '16px',
                                color: textColor,
                                '& .MuiSelect-select': {
                                  padding: '4px !important',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#002129',
                                  borderWidth: '2px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#002129',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#002129',
                                }
                              }}
                        >
                            {eventOptions?.map((option) => (
                                <MenuItem 
                                    key={option.tournamentId} 
                                    value={option.tournamentId}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    gap: '16px',
                    width: '90%',
                    maxWidth: '600px',
                    margin: '0 auto',
                    padding: '8px 0px 24px 0px',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: textColor,
                    }}
                >
                    For players without a score enter:<br/>
                    MC for missed cut<br/>
                    DQ for disqualified<br/>
                    WD for withdrew<br/>
                    NA if player did not play
                </Typography>
            </Box>

            {selectedEvent?.rounds > 1 && (
                <Box
                    sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    gap: '16px',
                    }}
                >
                    {Array.from({ length: selectedEvent.rounds }, (_, i) => {
                    const roundNumber = i + 1;
                    return (
                        <Button
                            key={roundNumber}
                            sx={{
                                backgroundColor: paramRound === String(roundNumber) ? '#E8E8E8' : '#fff',
                                color: '#000000',
                                '&:hover': {
                                    backgroundColor: '#BDBDBD',
                                },
                            }}
                            onClick={() => {
                                const newSearchParams = new URLSearchParams(searchParams);
                                newSearchParams.set('round', String(roundNumber));
                                setSearchParams(newSearchParams);
                            }}
                        >
                            Round {roundNumber}
                        </Button>
                    );
                    })}
                </Box>
            )}

            {teams?.map((team) => (
            <React.Fragment key={team.teamId}>
                <TeamScoreEntry
                    team={team}
                    playerState={playerState}
                    setPlayerState={setPlayerState}
                    round={paramRound}
                    event={selectedEvent}
                    eventId={paramEventId}
                    invalidEntries={invalidEntries}
                    setInvalidEntries={setInvalidEntries}
                    completedTeams={completedTeams}
                    setCompletedTeams={setCompletedTeams}
                />
                <Box sx={{ height: '48px' }} />
            </React.Fragment>
            ))}


            {!isPlayerStateEqual && (
                <ScoreSubmitBar 
                    invalidEntries={invalidEntries}
                    masterPoolId={masterPoolId}
                    completedTeamsCount={completedTeams?.length}
                    teamsCount={teams?.length}
                    round={paramRound}
                    eventId={paramEventId}
                    playerState={playerState}
                    setPlayerState={setPlayerState}
                    players={players}
                    selectedEvent={selectedEvent}
                    teams={teams}
                />
            )}
        </Box>
    );
}

export default ScoreEntryPage;